/**
 * Detecta reservas viejas ("zombies") sin actividad en N días.
 *
 * Una reserva es candidata a recordatorio cuando:
 *   - trade.reserved === true
 *   - Date.now() - updatedAt > threshold (default 7 días)
 *   - Y no está "snoozed" recientemente por el usuario
 *
 * El snooze se guarda en localStorage como un map { friendUid: ts }.
 * Cuando vence el snooze (default 3 días), el trade vuelve a aparecer.
 *
 * Esto ataca el caso real del usuario: te ves con alguien, reservas
 * un trato, pasan días, te olvidas y las láminas siguen bloqueadas
 * en tu inventario impidiendo que las uses en otros tratos.
 */

const DAY_MS = 24 * 60 * 60 * 1000;
const STALE_THRESHOLD_DAYS = 7;
const SNOOZE_DAYS = 3;
const SNOOZE_KEY_PREFIX = 'staleReservationSnooze_';

/** Parse timestamp tolerante a ISO string, number, o undefined. */
function parseTs(v) {
  if (!v) return 0;
  if (typeof v === 'number') return v;
  const parsed = Date.parse(v);
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * Devuelve la lista de tratos reservados que tienen >= staleDays sin
 * actividad, ordenados del más viejo al más reciente.
 *
 * @param {object} trades  state.trades
 * @param {object} friends state.friends (para resolver nombres)
 * @param {object} snoozeMap { friendUid: ts } — cargado de localStorage
 * @param {number} now      Date.now() (parametrizable para testing)
 * @param {number} staleDays threshold (default 7)
 *
 * @returns Array<{ friendUid, name, daysOld, giveCount, receiveCount }>
 */
export function findStaleReservations(trades, friends, snoozeMap = {}, now = Date.now(), staleDays = STALE_THRESHOLD_DAYS) {
  const threshold = staleDays * DAY_MS;
  const items = [];

  Object.entries(trades || {}).forEach(([friendUid, trade]) => {
    if (!trade || !trade.reserved) return;
    const ts = parseTs(trade.updatedAt);
    // Si no tiene updatedAt, asumimos que es muy viejo (caso legacy).
    const ageMs = ts === 0 ? Infinity : (now - ts);
    if (ageMs < threshold) return;

    // Snoozed: el usuario clickó "recordar después" recientemente.
    const snoozedUntil = snoozeMap[friendUid] || 0;
    if (snoozedUntil > now) return;

    const friendName = friends?.[friendUid]?.name
      || (friendUid.startsWith('ext-') ? 'Amigo externo' : 'Coleccionista');

    items.push({
      friendUid,
      name: friendName,
      daysOld: ts === 0 ? null : Math.floor(ageMs / DAY_MS),
      giveCount: Array.isArray(trade.give) ? trade.give.length : 0,
      receiveCount: Array.isArray(trade.receive) ? trade.receive.length : 0,
    });
  });

  // Más viejas primero (null = "muy vieja" va al final por convención)
  items.sort((a, b) => (b.daysOld ?? -1) - (a.daysOld ?? -1));
  return items;
}

/** Carga el snooze map de localStorage (devuelve {} si nada o error). */
export function loadSnoozeMap(uid) {
  if (!uid) return {};
  try {
    const raw = localStorage.getItem(SNOOZE_KEY_PREFIX + uid);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

/** Agrega un snooze para un trato (vence en SNOOZE_DAYS desde ahora). */
export function snoozeReservation(uid, friendUid) {
  if (!uid || !friendUid) return;
  try {
    const current = loadSnoozeMap(uid);
    current[friendUid] = Date.now() + SNOOZE_DAYS * DAY_MS;
    localStorage.setItem(SNOOZE_KEY_PREFIX + uid, JSON.stringify(current));
  } catch {
    // localStorage lleno o deshabilitado — silencioso, el banner volverá
    // a aparecer al próximo render. No es catastrófico.
  }
}

// Re-exports para tests
export const __internals = { parseTs, DAY_MS, STALE_THRESHOLD_DAYS, SNOOZE_DAYS };
